import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChatApi } from "../../../api/chatApi";
import {
  useAuthStore,
  useIsUserSignedIn,
} from "../../../core_stores/useAuthStore";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { useAuthModal } from "../../../providers/AuthModalProvid";
import { trackRedditEvent } from "../../../utils/analyticsUtils";
import { formatNumberWithSpaces } from "../../../utils/numberFormatter";
import {
  ALLOWED_IMAGE_TYPES,
  useAttachedImage,
  useIsPromptSubmitting,
  useMaxPromptLength,
  usePrompt,
  usePromptInputActions,
} from "../../../view_stores/usePromptInputStore";
import { AttachedImageIcon } from "../../common_components/AttachedImageIcon";
import { AttachmentsBtn } from "../../common_components/AttachmentsBtn";

export function WelcomeTextField() {
  const maxPromptLength = useMaxPromptLength();
  const prompt = usePrompt();
  const attachedImage = useAttachedImage();
  const isPromptSubmitting = useIsPromptSubmitting();
  const isUserSignedIn = useIsUserSignedIn();
  const isCompactView = useIsCompactView();
  const navigate = useNavigate();
  const location = useLocation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    setPrompt,
    setAttachedImage,
    setIsSubmitting,
    isLengthExceeded: isPromptLengthExceeded,
    clear: clearPromptInput,
  } = usePromptInputActions();
  const { show: showAuthModal } = useAuthModal();

  // Focus on textarea when navigated with focusTextField flag
  useEffect(() => {
    if (location.state?.focusTextField && textareaRef.current) {
      textareaRef.current.focus();
      // Clear the state to prevent refocusing on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Focus textarea by default when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAttachedImage(file);
    }
  };

  const handleRemoveImage = () => {
    setAttachedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;

    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file && ALLOWED_IMAGE_TYPES.includes(file.type)) {
          e.preventDefault();
          setAttachedImage(file);
          break;
        }
      }
    }
  };

  async function submit() {
    if (!prompt || prompt.trim() === "" || isPromptSubmitting) {
      return;
    }

    trackRedditEvent("generate_clicked");

    setIsSubmitting(true);
    try {
      if (!isUserSignedIn) {
        await showAuthModal();
        const isUserSignedInAfterAuthModal =
          useAuthStore.getState().user !== null;
        if (!isUserSignedInAfterAuthModal) {
          return;
        }
      }

      const projectId = await ChatApi.createNewProject();
      clearPromptInput();
      navigate(`/project/${projectId}`, {
        state: {
          projectId,
          initialPrompt: prompt,
          initialImage: attachedImage,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={`${isCompactView && "px-4"} w-full z-20`}>
      <div
        className="h-full bg-gradient-to-b 
      from-white dark:from-[#222528]
      to-light-surface-primary dark:to-[#30353A]
      rounded-lg p-2 space-y-1
      border border-white dark:border-[#222528]
      flex flex-col
      [box-shadow:0px_2px_21.2px_-1px_rgba(6,4,43,0.5),0px_2px_5.9px_0px_rgba(4,3,26,0.18)]"
      >
        <div className="relative w-full flex-1">
          <textarea
            className="w-full h-[125px]
            bg-white dark:bg-[#16191F] rounded-lg 
            border border-[#ADADAD] dark:border-[#373B3F]
            resize-none focus:outline-none 
            text-light-text dark:text-dark-text 
            py-2 px-3"
            value={prompt ?? ""}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            ref={textareaRef}
          />
          {!prompt && (
            <div className="absolute top-2 left-[14px] right-[14px] pointer-events-none z-0">
              <CustomPlaceholder onClick={handleUploadClick} />
            </div>
          )}
        </div>
        {isPromptLengthExceeded() && (
          <div className="text-red-500 text-sm px-2">
            Message is too long — max {formatNumberWithSpaces(maxPromptLength)}{" "}
            characters allowed.
          </div>
        )}
        <div className="flex justify-between items-center gap-4 p-2">
          <div className="flex items-center gap-4">
            <AttachmentsBtn
              className="bg-white dark:bg-black/10 hover:opacity-50 text-primary-blue rounded-full transition-opacity duration-200"
              onAddImageClick={handleUploadClick}
              iconSize={34}
            />
            {attachedImage && (
              <AttachedImageIcon
                attachedImg={attachedImage}
                onRemove={handleRemoveImage}
              />
            )}
          </div>

          <SubmitBtn onClick={submit} />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/jpg"
          onChange={handleImageUpload}
          className="hidden"
        />
      </div>
    </div>
  );
}

function CustomPlaceholder({ onClick }: { onClick: () => void }) {
  return (
    <div className="text-light-placeholder dark:text-dark-placeholder">
      Describe your mobile app — you can also{" "}
      <span
        id="home-upload-image-placeholder"
        className="text-light-text dark:text-dark-text underline cursor-pointer pointer-events-auto"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        upload an image
      </span>{" "}
      to guide the design.
    </div>
  );
}

function SubmitBtn({ onClick }: { onClick: () => void }) {
  const { isLengthExceeded: isPromptLengthExceeded } = usePromptInputActions();
  const isPromptSubmitting = useIsPromptSubmitting();

  return (
    <div className="h-9 flex gap-2">
      <button
        id="home-generate"
        className="px-4 py-1 bg-primary-blue text-white rounded-full hover:bg-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        onClick={onClick}
        disabled={isPromptSubmitting || isPromptLengthExceeded()}
      >
        {isPromptSubmitting && (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
        Build my app
      </button>
    </div>
  );
}
